/**
 * strokedPathPrimitive — the ONE "this entity plots as stroked path(s)" tail
 * shared by the top-level converters.
 *
 * Every converter whose entity is pure outline geometry ends the same way:
 * resolve the entity's plot style, honour the CTB/STB do-not-plot sentinel
 * (PDF-6.3b — `style.skip` ⇒ emit NOTHING), then wrap each built subpath in a
 * `PathPrimitive` carrying the entity's id, its resolved OCG layer (PDF-6.2a)
 * and `fill: "none"`. Repeating that tail per converter is one domain truth
 * copied N times: a change to the skip contract or to the layer fallback would
 * have to be made in every copy, and the first one missed plots geometry the
 * plot-style table says must not be plotted.
 *
 * So the tail lives here once and the converters supply only their GEOMETRY
 * (@codex PR #785 round 2, F1 — introduced when the heavy POLYLINE and the OLE
 * frame needed the identical tail; `splineConverter` converged onto it in the
 * same change rather than leaving a third copy behind).
 */

import type {
  CadConvertContext,
  CadPrimitive,
  PathSegment,
  Point2,
} from "../cadPrimitive";
import { resolvePlotStyle, type StyledEntity } from "./resolveStyle";

/** One built subpath in RAW world mm: where it starts, how it runs, is it closed. */
export interface StrokedPathSpec {
  start: Point2;
  segments: PathSegment[];
  closed: boolean;
}

/**
 * Wrap `paths` as stroked {@link CadPrimitive}s for `entity`, or `[]` when the
 * entity has no geometry OR its plot style says do-not-plot. A MESH-like entity
 * passes several specs (one per disjoint wire run); a simple outline passes one.
 */
export function strokedPathPrimitives(
  entity: StyledEntity & { id: string },
  ctx: CadConvertContext,
  paths: readonly StrokedPathSpec[],
): CadPrimitive[] {
  if (paths.length === 0) return [];
  const style = resolvePlotStyle(entity, ctx);
  // PDF-6.3b — CTB/STB do-not-plot honour.
  if (style.skip) return [];
  const layer = entity.layer ?? "0";
  return paths.map((path) => ({
    type: "path" as const,
    entityId: entity.id,
    layer,
    start: path.start,
    segments: path.segments,
    closed: path.closed,
    fill: "none" as const,
    style,
  }));
}
